import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Box, Typography } from '@mui/material';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CtaSection from '../components/cta-section/CtaSection.tsx';
import './prodotti.scss';

gsap.registerPlugin(ScrollTrigger);

const marchi = [
  { id: 1, nome: 'Corteva', logo: '/loghi/marchi/corteva.webp', link: 'https://www.corteva.it/' },
  { id: 2, nome: 'UPL', logo: '/loghi/marchi/UPL_header-logo.mp4', link: 'https://www.uplcorp.com/it' },
  { id: 3, nome: 'Basf', logo: '/loghi/marchi/basf.png', link: 'https://www.basf.com/it/it' },
  { id: 4, nome: 'Bayer', logo: '/loghi/marchi/bayer.png', link: 'https://www.bayer.com/it/it/italia-home' },
  { id: 5, nome: 'Adama', logo: '/loghi/marchi/adama.svg', link: 'https://www.adama.com/italia/it' },
  { id: 6, nome: 'Certis', logo: '/loghi/marchi/certis.png', link: 'https://certisbelchim.it/' },
  { id: 7, nome: 'Sumitomo', logo: '/loghi/marchi/somitomo.svg', link: 'https://www.sumitomo-chem.it/' },
  { id: 8, nome: 'Syngenta', logo: '/loghi/marchi/syngenta.svg', link: 'https://www.syngenta.it/' },
  { id: 9, nome: 'DEKALB', logo: '/loghi/marchi/dekalb.png', link: 'https://www.dekalb.it/index/index.html' },
  { id: 10, nome: 'Pioneer', logo: '/loghi/marchi/Pioneer.webp', link: 'https://www.pioneer.com/landing', invert: true },
  { id: 11, nome: 'Lidea', logo: '/loghi/marchi/lidea.png', link: 'https://lidea-seeds.it/' },
  { id: 12, nome: 'Yara', logo: '/loghi/marchi/yara.svg', link: 'https://www.yara.it/' },
  { id: 13, nome: 'Agribios', logo: '/loghi/marchi/agribios.png', link: 'https://agribiositaliana.it/' },
  { id: 14, nome: 'Ilsa', logo: '/loghi/marchi/ilsa.svg', link: 'https://www.ilsagroup.com/it/home/' },
  { id: 15, nome: 'FCP Cerea', logo: '/loghi/marchi/fcp-cerea.webp', link: 'https://fcpcerea.it/' },
];

function Prodotti() {
  const heroRef = useRef<HTMLDivElement>(null);
  const marchiTitleRef = useRef<HTMLHeadingElement>(null);
  const marchiGridRef = useRef<HTMLDivElement>(null);

  const animateSplitText = (element: HTMLElement) => {
    const text = element.textContent || '';
    element.innerHTML = text
      .split('')
      .map(char => char === ' ' ? ' ' : `<span class="split-char">${char}</span>`)
      .join('');

    const chars = element.querySelectorAll('.split-char');
    gsap.fromTo(chars,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      }
    );
  };

  useEffect(() => {
    if (heroRef.current) {
      gsap.fromTo(heroRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
      );
    }

    if (marchiTitleRef.current) {
      animateSplitText(marchiTitleRef.current);
    }

    if (marchiGridRef.current) {
      const cards = marchiGridRef.current.querySelectorAll('.marchio-card');
      gsap.fromTo(cards,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: marchiGridRef.current,
            start: "top 80%",
            toggleActions: "play none none none"
          }
        }
      );
    }

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 600);

    return () => {
      clearTimeout(refreshTimer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Prodotti | Fabbri Agricoltura</title>
        <meta name="description" content="Scopri i marchi che commercializziamo: prodotti selezionati per l'agricoltura di qualita." />
        <meta name="keywords" content="prodotti agricoli, marchi agricoltura, sementi, concimi, agrofarmaci" />
      </Helmet>

      <Box className="prodotti-page-agricola">
        {/* Hero Section */}
        <Box className="prodotti-hero-agricola" ref={heroRef}>
          <video
            className="hero-video-agricola"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/video/video4.mp4" type="video/mp4" />
          </video>
          <Box className="hero-overlay-agricola"></Box>
          <Container maxWidth="lg" className="hero-content-agricola">
            <Box className="hero-box-agricola">
              <Typography variant="h1" className="hero-title-agricola">
                I Prodotti
              </Typography>
              <Typography className="hero-description-agricola">
                Prodotti selezionati per garantire qualità e risultati
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Marchi Section */}
        <Box className="marchi-section">
          <Container maxWidth="lg">
            <Typography variant="h2" className="section-title-agricola split-text" ref={marchiTitleRef}>
              Aziende Partner
            </Typography>
            <Typography className="section-subtitle-agricola">
              Alcune delle principali collaborazioni:
            </Typography>

            <Box className="marchi-grid" ref={marchiGridRef}>
              {marchi.map((marchio) => (
                <Box key={marchio.id} className="marchio-card">
                  <Box className="marchio-logo">
                    {marchio.logo.endsWith('.mp4') ? (
                      <video autoPlay loop muted playsInline className="logo-video">
                        <source src={marchio.logo} type="video/mp4" />
                      </video>
                    ) : (
                      <img src={marchio.logo} alt={marchio.nome} className={`logo-img ${marchio.invert ? 'logo-invert' : ''}`} />
                    )}
                  </Box>
                  <Typography variant="h6" className="marchio-nome">
                    {marchio.nome}
                  </Typography>
                  <a href={marchio.link} target="_blank" rel="noopener noreferrer" className="marchio-cta">
                    Scopri di più
                  </a>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* CTA Section */}
        <CtaSection title="Sei interessato ai prodotti?" />
      </Box>
    </>
  );
}

export default Prodotti;
